import type { ReactNode } from 'react';
import { ReceiptText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { OverallStatusBadge } from '@/components/transactions/OverallStatusBadge';
import { cn } from '@/lib/utils';
import type {
  FundTransactionDetail,
  ReconciliationStateResponse,
} from '@/types/fund-transactions';
import { formatDate } from '@/utils/formatters';

import {
  STATUS_BADGE_VARIANT,
  STATUS_LABEL,
} from './transactionDetailsConstants';
import { Field, VerificationStatusBadge } from './transactionDetails';

export function TransactionDetailsCard({
  transaction,
  fundName,
  reconciliation,
  onViewJournalEntries,
  icon,
  iconClassName,
  eyebrow,
  actions,
  fields,
  descriptionAside,
}: {
  transaction: FundTransactionDetail;
  fundName: string;
  reconciliation: ReconciliationStateResponse | undefined;
  onViewJournalEntries?: () => void;
  icon: ReactNode;
  iconClassName: string;
  eyebrow: string;
  actions?: ReactNode;
  fields: ReactNode;
  /** Shown beside the description, which narrows to make room. */
  descriptionAside?: ReactNode;
}) {
  return (
    <div className="py-5 space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className={cn('p-2.5 rounded-lg shrink-0', iconClassName)}
            aria-hidden
          >
            {icon}
          </div>
          <div className="min-w-0 space-y-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {eyebrow}
            </div>
            <div className="text-sm font-medium truncate">{fundName}</div>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <OverallStatusBadge status={transaction.overall_status ?? null} />
              <VerificationStatusBadge
                status={transaction.status}
                reconciliation={reconciliation}
              />
              {/* The plain status badge is redundant once OverallStatusBadge
                  renders (projection rows). Show it only when there is no
                  overall status, i.e. the overall badge renders nothing. */}
              {!transaction.overall_status && (
                <Badge variant={STATUS_BADGE_VARIANT[transaction.status]}>
                  {STATUS_LABEL[transaction.status]}
                </Badge>
              )}
            </div>
          </div>
        </div>

        {(actions || onViewJournalEntries) && (
          <div className="shrink-0 flex items-center gap-2">
            {actions}
            {onViewJournalEntries && (
              <Button
                variant="outline"
                size="sm"
                onClick={onViewJournalEntries}
                data-testid="view-journal-entries-button"
              >
                <ReceiptText className="h-4 w-4 mr-2" />
                Journal entries
              </Button>
            )}
          </div>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
        {fields}
        <Field label="Date">{formatDate(transaction.transaction_date)}</Field>
        {transaction.description?.trim() && (
          <Field
            label="Description"
            span={descriptionAside ? 'TWO_COLUMNS' : 'FULL_ROW'}
          >
            {transaction.description}
          </Field>
        )}
        {descriptionAside}
      </dl>
    </div>
  );
}
